import React from "react";
import { STEP_TITLES, WizardStep, wizardStepFor, isKnownImportStatus } from "../../../lib/importStatus";
import { CheckCircle2 } from "lucide-react";

/**
 * Status-driven 5-dot stepper. The active step is derived from the job's
 * backend status via `wizardStepFor(status)`. When `status` is falsy (creation
 * flow before a job exists) the stepper stays on Step 1. Unknown statuses
 * hide the stepper — the parent renders a safe recovery screen instead.
 */
export function WizardStepper({ status }: { status?: string | null }) {
  const active: WizardStep = !status ? 1 : (wizardStepFor(status) ?? 1);
  const showWarning = !!status && !isKnownImportStatus(status);
  if (showWarning) return null;

  const steps: { n: WizardStep; label: string }[] = [1, 2, 3, 4, 5].map(
    (n) => ({ n: n as WizardStep, label: STEP_TITLES[n as WizardStep] }),
  );

  return (
    <nav aria-label="Import wizard progress" data-testid="wizard-stepper">
      <ol className="flex items-center gap-2 flex-wrap">
        {steps.map(({ n, label }, i) => {
          const isCurrent = n === active;
          const isDone = n < active;
          const stateCls = isCurrent
            ? "bg-slate-900 text-white border-slate-900"
            : isDone
            ? "bg-emerald-600 text-white border-emerald-600"
            : "bg-white text-slate-500 border-slate-300";
          return (
            <React.Fragment key={n}>
              <li
                className="flex items-center gap-2"
                aria-current={isCurrent ? "step" : undefined}
                data-testid={`stepper-item-${n}`}
                data-active={isCurrent ? "true" : "false"}
                data-done={isDone ? "true" : "false"}
              >
                <span
                  className={`inline-flex items-center justify-center w-7 h-7 rounded-full border text-xs font-semibold ${stateCls}`}
                  aria-hidden={isCurrent ? undefined : true}
                >
                  {isDone ? <CheckCircle2 className="w-4 h-4" aria-hidden /> : n}
                  <span className="sr-only">
                    Step {n}
                    {isDone ? " (completed)" : isCurrent ? " (current)" : ""}
                  </span>
                </span>
                <span
                  className={`text-sm hidden sm:inline ${isCurrent ? "font-semibold text-slate-900" : "text-slate-500"}`}
                >
                  {label}
                </span>
                <span
                  className={`text-sm sm:hidden ${isCurrent ? "font-semibold text-slate-900" : "text-slate-500 sr-only"}`}
                  aria-hidden={!isCurrent}
                >
                  {label}
                </span>
              </li>
              {i < steps.length - 1 && (
                <span aria-hidden className="flex-1 min-w-4 h-px bg-slate-200" />
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
